import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LuBotMessageSquare, LuSend, LuSparkles, LuX } from "react-icons/lu";
import { retention } from "../legal/company";

const FloatingContainer = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 95;

  @media (max-width: 640px) {
    right: 16px;
    bottom: 16px;
  }
`;

const ChatButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 52px;
  padding: 0 20px 0 16px;
  border: none;
  border-radius: 999px;
  background: var(--color-primary);
  color: var(--color-on-primary);
  font-size: 0.9375rem;
  font-weight: 600;
  box-shadow: 0 12px 28px -10px rgba(30, 68, 240, 0.55);
  transition: background-color 0.2s ease;

  svg {
    width: 22px;
    height: 22px;
  }

  &:hover {
    background: var(--color-primary-hover);
  }

  @media (max-width: 640px) {
    width: 52px;
    padding: 0;
    justify-content: center;

    span {
      display: none;
    }
  }
`;

const ChatPopup = styled(motion.div)`
  position: absolute;
  right: 0;
  bottom: 68px;
  display: flex;
  flex-direction: column;
  width: min(392px, calc(100vw - 32px));
  height: min(580px, calc(100vh - 120px));
  overflow: hidden;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  transform-origin: bottom right;
`;

const PopupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 14px 16px;
  border-bottom: 1px solid var(--color-border);
`;

const HeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--radius);
  background: var(--color-primary-soft);
  color: var(--color-primary);

  svg {
    width: 20px;
    height: 20px;
  }
`;

const OnlineIndicator = styled.span`
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #16a34a;
  border: 2px solid var(--color-surface);
`;

const HeaderText = styled.div`
  line-height: 1.35;

  h2 {
    font-size: 0.9375rem;
    letter-spacing: 0;
  }

  p {
    font-size: 0.8125rem;
    color: var(--color-muted);
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--color-muted);
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
    background: var(--color-bg-subtle);
    color: var(--color-ink);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 16px;
  background: var(--color-bg-subtle);
`;

const blink = keyframes`
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
`;

const Message = styled.div`
  display: flex;
  justify-content: ${props => (props.$isUser ? "flex-end" : "flex-start")};

  .message-content {
    max-width: 85%;
    padding: 10px 14px;
    border-radius: 14px;
    border-bottom-${props => (props.$isUser ? "right" : "left")}-radius: 4px;
    background: ${props =>
      props.$isUser ? "var(--color-primary)" : "var(--color-surface)"};
    border: 1px solid
      ${props => (props.$isUser ? "var(--color-primary)" : "var(--color-border)")};
    color: ${props =>
      props.$isUser ? "var(--color-on-primary)" : "var(--color-ink)"};
    font-size: 0.9375rem;
    line-height: 1.6;
    white-space: pre-wrap;
    word-wrap: break-word;

    a {
      color: ${props =>
        props.$isUser ? "var(--color-on-primary)" : "var(--color-primary)"};
      text-decoration: underline;
      text-underline-offset: 2px;
      font-weight: 500;
    }

    &.streaming::after {
      content: "▋";
      margin-left: 2px;
      color: var(--color-primary);
      animation: ${blink} 1s infinite;
    }
  }
`;

// Function to convert URLs and phone numbers to clickable links
const formatMessageWithLinks = text => {
  if (!text) return "";

  let formattedText = text;

  // Handle WhatsApp links - convert to friendly format
  formattedText = formattedText.replace(
    /📲 Contact us on WhatsApp: (\+\d+) \((https:\/\/wa\.me\/\d+)\)/g,
    '📲 Contact us on <a href="$2" target="_blank" rel="noopener noreferrer">WhatsApp: $1</a>'
  );

  // Handle Instagram links - convert to friendly format
  formattedText = formattedText.replace(
    /📸 Instagram: (@[^\s]+) \((https:\/\/instagram\.com\/[^)]+)\)/g,
    '📸 Instagram: <a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  // Handle email addresses
  formattedText = formattedText.replace(
    /📧 Email: ([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
    '📧 Email: <a href="mailto:$1">$1</a>'
  );

  // Handle phone numbers
  formattedText = formattedText.replace(
    /📞 Phone: (\+\d+)/g,
    '📞 Phone: <a href="tel:$1">$1</a>'
  );

  // Handle any remaining URLs that weren't caught by specific patterns
  formattedText = formattedText.replace(
    /(https?:\/\/[^\s)]+)(?![^<]*>)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  // Handle any remaining standalone email addresses (avoid double linking)
  formattedText = formattedText.replace(
    /\b([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})\b(?![^<]*>)/g,
    '<a href="mailto:$1">$1</a>'
  );

  return formattedText;
};

const InputArea = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 12px;
  border-top: 1px solid var(--color-border);
  background: var(--color-surface);
`;

const ChatInput = styled.textarea`
  flex: 1;
  min-height: 44px;
  max-height: 120px;
  padding: 11px 14px;
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius);
  font-size: 0.9375rem;
  line-height: 1.4;
  resize: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(30, 68, 240, 0.18);
  }

  &::placeholder {
    color: #8a96a8;
  }
`;

const SendButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: var(--radius);
  background: var(--color-primary);
  color: var(--color-on-primary);
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-primary-hover);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const WelcomeMessage = styled.div`
  padding: 12px 4px;

  > svg {
    width: 24px;
    height: 24px;
    color: var(--color-primary);
    margin-bottom: 12px;
  }

  h3 {
    font-size: 1.0625rem;
    margin-bottom: 6px;
  }

  p {
    font-size: 0.9375rem;
    color: var(--color-muted);
    margin-bottom: 20px;
  }
`;

const Suggestions = styled.div`
  display: grid;
  gap: 8px;
`;

const privacyLinkStyles = `
  a {
    color: var(--color-primary);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const PrivacyNote = styled.p`
  && {
    margin: 20px 0 0;
    font-size: 0.8125rem;
    line-height: 1.55;
    color: var(--color-muted);
  }

  ${privacyLinkStyles}
`;

const InputCaption = styled.p`
  padding: 0 12px 10px;
  background: var(--color-surface);
  font-size: 0.75rem;
  color: var(--color-muted);

  ${privacyLinkStyles}
`;

const Suggestion = styled.button`
  padding: 10px 14px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  background: var(--color-surface);
  font-size: 0.875rem;
  text-align: left;
  color: var(--color-ink);
  transition: border-color 0.2s ease, color 0.2s ease;

  &:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }
`;

// Streaming API function with typing effect
const sendMessageToAPI = async (message, onProgress) => {
  try {
    const url =
      process.env.REACT_APP_NODE_ENV === "production"
        ? process.env.REACT_APP_API_URL +
          `/api/stream?message=${encodeURIComponent(message)}`
        : `http://localhost:5000/api/stream?message=${encodeURIComponent(
            message
          )}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "text/event-stream",
        "Cache-Control": "no-cache",
        Authorization: `Bearer ${process.env.REACT_APP_FRONTEND_SECRET}`, // or read from server-rendered config
      },
    });
    if (!response.ok) {
      throw new Error("API request failed");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let fullText = "";

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");

        // Keep the last potentially incomplete line in the buffer
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const jsonData = line.slice(6).trim();
              if (jsonData) {
                const data = JSON.parse(jsonData);
                if (data.delta) {
                  fullText += data.delta;
                  // Call the progress callback with the current accumulated text
                  if (onProgress) {
                    onProgress(fullText);
                  }
                }
                if (data.done) {
                  return fullText;
                }
              }
            } catch (e) {
              // Ignore malformed JSON
              console.warn("Failed to parse SSE data:", line);
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    return fullText;
  } catch (error) {
    console.error("Error sending message:", error);
    throw new Error(
      "Sorry, I'm having trouble connecting right now.\n\nPlease try again later or contact us directly on WhatsApp: +212 660-577967"
    );
  }
};

const ChatContent = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesAreaRef = useRef(null);

  const suggestions = [
    "Tell me about your services",
    "What technologies do you use?",
    "How can I contact you?",
    "Show me your portfolio",
  ];

  // Keep the latest message in view. Scrolls only the message list (not the
  // page) and leaves the welcome screen at its top.
  useEffect(() => {
    const area = messagesAreaRef.current;
    if (!area || messages.length === 0) return;
    area.scrollTo({ top: area.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue("");
    setMessages(prev => [...prev, { text: userMessage, isUser: true }]);
    setIsLoading(true);

    // Add an empty AI message that will be updated with streaming content
    setMessages(prev => [
      ...prev,
      { text: "", isUser: false, isStreaming: true },
    ]);

    try {
      await sendMessageToAPI(userMessage, progressText => {
        // Update the AI message with the progressively received text
        setMessages(prev => {
          const newMessages = [...prev];
          const lastMessageIndex = newMessages.length - 1;
          if (
            newMessages[lastMessageIndex] &&
            !newMessages[lastMessageIndex].isUser
          ) {
            newMessages[lastMessageIndex] = {
              text: progressText,
              isUser: false,
              isStreaming: true,
            };
          }
          return newMessages;
        });
      });

      // Mark streaming as complete
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessageIndex = newMessages.length - 1;
        if (
          newMessages[lastMessageIndex] &&
          !newMessages[lastMessageIndex].isUser
        ) {
          newMessages[lastMessageIndex] = {
            ...newMessages[lastMessageIndex],
            isStreaming: false,
          };
        }
        return newMessages;
      });
    } catch (error) {
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessageIndex = newMessages.length - 1;
        if (
          newMessages[lastMessageIndex] &&
          !newMessages[lastMessageIndex].isUser
        ) {
          newMessages[lastMessageIndex] = {
            text:
              error.message ||
              "Sorry, I'm having trouble connecting right now. Please try again later.",
            isUser: false,
            isStreaming: false,
          };
        }
        return newMessages;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = suggestion => {
    setInputValue(suggestion);
  };

  const handleKeyDown = e => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <ChatContainer>
      <MessagesArea ref={messagesAreaRef}>
        {messages.length === 0 ? (
          <WelcomeMessage>
            <LuSparkles aria-hidden="true" />
            <h3>Hi! I'm the Code Solutions assistant</h3>
            <p>
              Ask me about our services, portfolio and experience. I'm happy
              to help.
            </p>
            <Suggestions>
              {suggestions.map(suggestion => (
                <Suggestion
                  key={suggestion}
                  type="button"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </Suggestion>
              ))}
            </Suggestions>
            <PrivacyNote>
              Your messages are processed by OpenAI to generate replies and
              stored for {retention.chat}. Please don't share sensitive
              information. <Link to="/privacy-policy">Privacy Policy</Link>
            </PrivacyNote>
          </WelcomeMessage>
        ) : (
          messages.map((message, index) => (
            <Message key={index} $isUser={message.isUser}>
              <div
                className={`message-content ${
                  message.isStreaming ? "streaming" : ""
                }`}
                dangerouslySetInnerHTML={{
                  __html: formatMessageWithLinks(message.text),
                }}
              />
            </Message>
          ))
        )}
      </MessagesArea>

      <InputArea>
        <label htmlFor="chat-input" className="sr-only">
          Message
        </label>
        <ChatInput
          id="chat-input"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your message…"
          rows="1"
        />
        <SendButton
          type="button"
          onClick={handleSendMessage}
          disabled={isLoading || !inputValue.trim()}
          aria-label="Send message"
        >
          <LuSend aria-hidden="true" />
        </SendButton>
      </InputArea>
      <InputCaption>
        AI answers may be inaccurate. <Link to="/privacy-policy">Privacy</Link>
      </InputCaption>
    </ChatContainer>
  );
};

const AIChatFloat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Show the button shortly after the page loads
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKey = e => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen]);

  if (!isVisible) return null;

  return (
    <FloatingContainer>
      <AnimatePresence>
        {isOpen && (
          <ChatPopup
            id="ai-chat"
            role="dialog"
            aria-label="Chat with the Code Solutions assistant"
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            <PopupHeader>
              <HeaderInfo>
                <Avatar>
                  <LuBotMessageSquare aria-hidden="true" />
                  <OnlineIndicator />
                </Avatar>
                <HeaderText>
                  <h2>AI Assistant</h2>
                  <p>Online · Here to help</p>
                </HeaderText>
              </HeaderInfo>
              <CloseButton
                type="button"
                onClick={() => setIsOpen(false)}
                aria-label="Close chat"
              >
                <LuX aria-hidden="true" />
              </CloseButton>
            </PopupHeader>

            <ChatContent />
          </ChatPopup>
        )}
      </AnimatePresence>

      <ChatButton
        type="button"
        onClick={() => setIsOpen(open => !open)}
        aria-expanded={isOpen}
        aria-controls="ai-chat"
        aria-label={isOpen ? "Close chat" : "Chat with our AI assistant"}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        whileTap={{ scale: 0.97 }}
      >
        {isOpen ? (
          <LuX aria-hidden="true" />
        ) : (
          <LuBotMessageSquare aria-hidden="true" />
        )}
        <span>{isOpen ? "Close" : "Ask our AI"}</span>
      </ChatButton>
    </FloatingContainer>
  );
};

export default AIChatFloat;
